import { useState, useEffect, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { authMe, clearSessionToken, authFetch } from "@/lib/api";
import { Loader2 } from "lucide-react";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ProtectedRoute = ({ children, requireSubscription = true }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(location.state?.user ? true : null);
  const [user, setUser] = useState(location.state?.user || null);
  const [hasAccess, setHasAccess] = useState(null);

  const checkSubscription = useCallback(async () => {
    try {
      const subRes = await authFetch(`${API_BASE}/stripe/subscription-status`);
      if (subRes.ok) {
        const subData = await subRes.json();
        if (subData.has_access) {
          setHasAccess(true);
        } else {
          setHasAccess(false);
          navigate('/subscription', { replace: true });
        }
      } else {
        setHasAccess(false);
        navigate('/subscription', { replace: true });
      }
    } catch {
      setHasAccess(false);
      navigate('/subscription', { replace: true });
    }
  }, [navigate]);

  const checkAuth = useCallback(async () => {
    if (window.location.hash?.includes('session_id=')) return;
    try {
      const res = await authMe();
      setUser(res.data);
      setIsAuthenticated(true);

      if (requireSubscription) {
        await checkSubscription();
      } else {
        setHasAccess(true);
      }
    } catch {
      clearSessionToken();
      setIsAuthenticated(false);
      navigate('/', { replace: true });
    }
  }, [navigate, requireSubscription, checkSubscription]);

  useEffect(() => {
    if (location.state?.user && !requireSubscription) {
      setHasAccess(true);
      return;
    }
    if (location.state?.user && requireSubscription) {
      setIsAuthenticated(true);
      checkSubscription();
      return;
    }
    checkAuth();
  }, [location.state, checkAuth, requireSubscription, checkSubscription]);

  if (isAuthenticated === null || (requireSubscription && hasAccess === null)) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#F9F8F6' }}>
        <div className="text-center space-y-4">
          <Loader2 className="w-10 h-10 animate-spin mx-auto" style={{ color: '#2D6A4F' }} />
          <p className="text-sm" style={{ color: '#4A4A4A' }}>Verification de la session...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) return null;
  if (requireSubscription && !hasAccess) return null;

  return typeof children === 'function' ? children({ user }) : children;
};

export default ProtectedRoute;
